import { useQuery } from "@tanstack/react-query";
import { api, type Api } from "@api/bot-base-api-request";
import {
  TransactionBotFilters,
  TransactionFilters,
  TransactionsBotResponse,
  TransactionSort,
} from "./types";
import { Wallet } from "@/providers/wallet";
import { LOGO_URL } from "@/constants";

export const TRANSACTIONS_BOT_QUERY_KEY = "transactions-bot";

const fetchTransactions = async (
  api: Api,
  address: string,
  sort: TransactionSort,
  filters?: TransactionBotFilters,
  search: string = "",
  pageNumber: number = 1,
  limit: number = 100
): Promise<TransactionsBotResponse> => {
  const searchParams: Record<string, string> = {
    ...Object.fromEntries(
      Object.entries(filters || {})
        .filter(([, v]) => v !== undefined)
        .map(([k, v]) => [k, String(v)])
    ),
    walletAddress: address,
    sortField: sort.sortField,
    sortType: sort.sortType,
    page: String(pageNumber),
    limit: String(limit),
    ...(search !== "" ? { searchName: search } : {}),
  };

  return api
    .get("transactions", {
      searchParams,
    })
    .json();
};

const mapFilters = (filters: TransactionFilters): TransactionBotFilters => {
  const mapped: TransactionBotFilters = {};

  if (filters.transactionType === "buy") mapped.isBuy = true;
  if (filters.transactionType === "sell") mapped.isSell = true;

  if (filters.ageHoursMin)
    mapped.fromTimestamp = String(
      Math.floor(Date.now() / 1000) - Number(filters.ageHoursMin) * 3600
    );
  if (filters.ageHoursMax)
    mapped.toTimestamp = String(
      Math.floor(Date.now() / 1000) - Number(filters.ageHoursMax) * 3600
    );

  if (filters.priceMin) mapped.minPrice = filters.priceMin;
  if (filters.priceMax) mapped.maxPrice = filters.priceMax;

  if (filters.valueMin) mapped.minValue = filters.valueMin;
  if (filters.valueMax) mapped.maxValue = filters.valueMax;

  return mapped;
};

const useGetBotTransactions = (
  wallet: Wallet,
  page: number,
  filters: TransactionFilters,
  search: string,
  sort: TransactionSort
) => {
  const botFilters = mapFilters(filters);

  return useQuery({
    queryKey: [
      TRANSACTIONS_BOT_QUERY_KEY,
      wallet.address,
      wallet.chain,
      filters,
      page,
      search,
      sort.sortField,
      sort.sortType,
    ],
    queryFn: async () =>
      fetchTransactions(api, wallet.address, sort, botFilters, search, page),
    select: (data) => ({
      ...data,
      transactions: data.transactions.map((tx) => ({
        ...tx,
        amountFormatted: Number(tx.tokenAmount) / 10 ** tx.tokenDecimals,
        tokenImage: `${LOGO_URL}/${wallet.walletType}/${tx.token}.jpg`,
      })),
    }),
  });
};

export { useGetBotTransactions };
